import { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { 
  Calculator, 
  Home, 
  Utensils, 
  Bus, 
  Wifi, 
  Dumbbell,
  ShoppingBag,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Info
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

// Cost of living data scraped from Numbeo (Feb 2026)
const costData = {
  usa: {
    currency: 'USD',
    symbol: '$',
    cities: {
      'New York': {
        rent1BedCenter: 4478,
        rent1BedOutside: 2769,
        utilities: 194,
        internet: 68,
        transportPass: 133,
        mealInexpensive: 25,
        mealMidRange: 140,
        groceriesMonthly: 400,
        gymMembership: 110,
        entertainment: 100,
        singlePersonMonthly: 1635
      },
      'Los Angeles': {
        rent1BedCenter: 2800,
        rent1BedOutside: 2100,
        utilities: 150,
        internet: 65,
        transportPass: 100,
        mealInexpensive: 20,
        mealMidRange: 100,
        groceriesMonthly: 350,
        gymMembership: 50,
        entertainment: 80,
        singlePersonMonthly: 1400
      },
      'Boston': {
        rent1BedCenter: 3200,
        rent1BedOutside: 2400,
        utilities: 180,
        internet: 70,
        transportPass: 90,
        mealInexpensive: 22,
        mealMidRange: 110,
        groceriesMonthly: 380,
        gymMembership: 60,
        entertainment: 90,
        singlePersonMonthly: 1500
      }
    }
  },
  uk: {
    currency: 'GBP',
    symbol: '£',
    cities: {
      'London': {
        rent1BedCenter: 2337,
        rent1BedOutside: 1734,
        utilities: 287,
        internet: 32,
        transportPass: 180,
        mealInexpensive: 20,
        mealMidRange: 80,
        groceriesMonthly: 300,
        gymMembership: 56,
        entertainment: 80,
        singlePersonMonthly: 1467
      },
      'Manchester': {
        rent1BedCenter: 1200,
        rent1BedOutside: 900,
        utilities: 200,
        internet: 30,
        transportPass: 100,
        mealInexpensive: 15,
        mealMidRange: 60,
        groceriesMonthly: 250,
        gymMembership: 35,
        entertainment: 60,
        singlePersonMonthly: 1100
      },
      'Edinburgh': {
        rent1BedCenter: 1400,
        rent1BedOutside: 1000,
        utilities: 220,
        internet: 30,
        transportPass: 65,
        mealInexpensive: 15,
        mealMidRange: 65,
        groceriesMonthly: 260,
        gymMembership: 40,
        entertainment: 65,
        singlePersonMonthly: 1150
      }
    }
  },
  canada: {
    currency: 'CAD',
    symbol: 'C$',
    cities: {
      'Toronto': {
        rent1BedCenter: 2315,
        rent1BedOutside: 2008,
        utilities: 207,
        internet: 75,
        transportPass: 156,
        mealInexpensive: 25,
        mealMidRange: 120,
        groceriesMonthly: 400,
        gymMembership: 81,
        entertainment: 80,
        singlePersonMonthly: 1556
      },
      'Vancouver': {
        rent1BedCenter: 2600,
        rent1BedOutside: 2100,
        utilities: 120,
        internet: 80,
        transportPass: 110,
        mealInexpensive: 22,
        mealMidRange: 100,
        groceriesMonthly: 380,
        gymMembership: 60,
        entertainment: 75,
        singlePersonMonthly: 1400
      },
      'Montreal': {
        rent1BedCenter: 1800,
        rent1BedOutside: 1400,
        utilities: 100,
        internet: 70,
        transportPass: 94,
        mealInexpensive: 20,
        mealMidRange: 85,
        groceriesMonthly: 320,
        gymMembership: 45,
        entertainment: 60,
        singlePersonMonthly: 1200
      }
    }
  },
  australia: {
    currency: 'AUD',
    symbol: 'A$',
    cities: {
      'Sydney': {
        rent1BedCenter: 3454,
        rent1BedOutside: 2411,
        utilities: 294,
        internet: 79,
        transportPass: 217,
        mealInexpensive: 30,
        mealMidRange: 132,
        groceriesMonthly: 450,
        gymMembership: 104,
        entertainment: 100,
        singlePersonMonthly: 1776
      },
      'Melbourne': {
        rent1BedCenter: 2400,
        rent1BedOutside: 1800,
        utilities: 200,
        internet: 75,
        transportPass: 170,
        mealInexpensive: 25,
        mealMidRange: 110,
        groceriesMonthly: 400,
        gymMembership: 70,
        entertainment: 80,
        singlePersonMonthly: 1500
      },
      'Brisbane': {
        rent1BedCenter: 2200,
        rent1BedOutside: 1600,
        utilities: 180,
        internet: 70,
        transportPass: 150,
        mealInexpensive: 22,
        mealMidRange: 100,
        groceriesMonthly: 380,
        gymMembership: 60,
        entertainment: 70,
        singlePersonMonthly: 1400
      }
    }
  },
  germany: {
    currency: 'EUR',
    symbol: '€',
    cities: {
      'Berlin': {
        rent1BedCenter: 1288,
        rent1BedOutside: 913,
        utilities: 338,
        internet: 44,
        transportPass: 63,
        mealInexpensive: 15,
        mealMidRange: 60,
        groceriesMonthly: 250,
        gymMembership: 31,
        entertainment: 60,
        singlePersonMonthly: 1002
      },
      'Munich': {
        rent1BedCenter: 1800,
        rent1BedOutside: 1300,
        utilities: 300,
        internet: 45,
        transportPass: 80,
        mealInexpensive: 18,
        mealMidRange: 75,
        groceriesMonthly: 280,
        gymMembership: 45,
        entertainment: 70,
        singlePersonMonthly: 1200
      },
      'Frankfurt': {
        rent1BedCenter: 1500,
        rent1BedOutside: 1100,
        utilities: 280,
        internet: 45,
        transportPass: 110,
        mealInexpensive: 16,
        mealMidRange: 70,
        groceriesMonthly: 270,
        gymMembership: 40,
        entertainment: 65,
        singlePersonMonthly: 1100
      }
    }
  },
  newzealand: {
    currency: 'NZD',
    symbol: 'NZ$',
    cities: {
      'Auckland': {
        rent1BedCenter: 2186,
        rent1BedOutside: 1968,
        utilities: 274,
        internet: 85,
        transportPass: 220,
        mealInexpensive: 25,
        mealMidRange: 130,
        groceriesMonthly: 400,
        gymMembership: 65,
        entertainment: 80,
        singlePersonMonthly: 1768
      },
      'Wellington': {
        rent1BedCenter: 2000,
        rent1BedOutside: 1700,
        utilities: 250,
        internet: 80,
        transportPass: 180,
        mealInexpensive: 22,
        mealMidRange: 110,
        groceriesMonthly: 380,
        gymMembership: 55,
        entertainment: 70,
        singlePersonMonthly: 1600
      },
      'Christchurch': {
        rent1BedCenter: 1600,
        rent1BedOutside: 1400,
        utilities: 220,
        internet: 75,
        transportPass: 100,
        mealInexpensive: 20,
        mealMidRange: 90,
        groceriesMonthly: 350,
        gymMembership: 45,
        entertainment: 60,
        singlePersonMonthly: 1400
      }
    }
  },
  dubai: {
    currency: 'AED',
    symbol: 'AED ',
    cities: {
      'Dubai': {
        rent1BedCenter: 7500,
        rent1BedOutside: 5000,
        utilities: 600,
        internet: 400,
        transportPass: 350,
        mealInexpensive: 40,
        mealMidRange: 200,
        groceriesMonthly: 1500,
        gymMembership: 350,
        entertainment: 500,
        singlePersonMonthly: 4500
      },
      'Abu Dhabi': {
        rent1BedCenter: 6000,
        rent1BedOutside: 4000,
        utilities: 500,
        internet: 380,
        transportPass: 300,
        mealInexpensive: 35,
        mealMidRange: 180,
        groceriesMonthly: 1400,
        gymMembership: 300,
        entertainment: 400,
        singlePersonMonthly: 4000
      },
      'Sharjah': {
        rent1BedCenter: 3500,
        rent1BedOutside: 2500,
        utilities: 400,
        internet: 350,
        transportPass: 200,
        mealInexpensive: 30,
        mealMidRange: 150,
        groceriesMonthly: 1200,
        gymMembership: 200,
        entertainment: 300,
        singlePersonMonthly: 3200
      }
    }
  },

  // ── Batch 1 fact-check fix: previously these five countries fell through
  // to the USA dataset because they weren't keyed here, so every "cost of
  // living in Ireland/France/Singapore/Malaysia/New Zealand" calculator
  // silently showed New York USD figures. Values below are 2025-26 range
  // estimates from Numbeo/Expatistan (student-level 1-bed rents, monthly
  // student budget in local currency).
  ireland: {
    currency: 'EUR',
    symbol: '€',
    cities: {
      'Dublin':   { rent1BedCenter: 2350, rent1BedOutside: 1850, utilities: 210, internet: 55, transportPass: 100, mealInexpensive: 18, mealMidRange: 65, groceriesMonthly: 380, gymMembership: 55, entertainment: 120, singlePersonMonthly: 1400 },
      'Cork':     { rent1BedCenter: 1450, rent1BedOutside: 1150, utilities: 190, internet: 50, transportPass: 90,  mealInexpensive: 16, mealMidRange: 55, groceriesMonthly: 340, gymMembership: 45, entertainment: 100, singlePersonMonthly: 1100 },
      'Galway':   { rent1BedCenter: 1400, rent1BedOutside: 1100, utilities: 180, internet: 50, transportPass: 85,  mealInexpensive: 15, mealMidRange: 50, groceriesMonthly: 320, gymMembership: 45, entertainment: 100, singlePersonMonthly: 1050 },
      'Limerick': { rent1BedCenter: 1250, rent1BedOutside: 950,  utilities: 175, internet: 50, transportPass: 80,  mealInexpensive: 14, mealMidRange: 45, groceriesMonthly: 310, gymMembership: 40, entertainment:  90, singlePersonMonthly: 950 },
    },
  },

  'new-zealand': {
    currency: 'NZD',
    symbol: 'NZ$',
    cities: {
      'Auckland':      { rent1BedCenter: 2400, rent1BedOutside: 1900, utilities: 220, internet: 90, transportPass: 220, mealInexpensive: 22, mealMidRange: 80, groceriesMonthly: 450, gymMembership: 75, entertainment: 130, singlePersonMonthly: 1650 },
      'Wellington':    { rent1BedCenter: 2200, rent1BedOutside: 1750, utilities: 200, internet: 85, transportPass: 200, mealInexpensive: 20, mealMidRange: 70, groceriesMonthly: 420, gymMembership: 70, entertainment: 120, singlePersonMonthly: 1500 },
      'Christchurch':  { rent1BedCenter: 1900, rent1BedOutside: 1500, utilities: 190, internet: 80, transportPass: 180, mealInexpensive: 20, mealMidRange: 65, groceriesMonthly: 400, gymMembership: 65, entertainment: 110, singlePersonMonthly: 1350 },
      'Dunedin':       { rent1BedCenter: 1600, rent1BedOutside: 1300, utilities: 175, internet: 80, transportPass: 160, mealInexpensive: 18, mealMidRange: 60, groceriesMonthly: 380, gymMembership: 60, entertainment: 100, singlePersonMonthly: 1200 },
      'Hamilton':      { rent1BedCenter: 1550, rent1BedOutside: 1250, utilities: 170, internet: 80, transportPass: 155, mealInexpensive: 18, mealMidRange: 58, groceriesMonthly: 370, gymMembership: 60, entertainment:  95, singlePersonMonthly: 1150 },
    },
  },

  france: {
    currency: 'EUR',
    symbol: '€',
    cities: {
      'Paris':       { rent1BedCenter: 1450, rent1BedOutside: 1050, utilities: 180, internet: 35, transportPass: 85, mealInexpensive: 15, mealMidRange: 55, groceriesMonthly: 350, gymMembership: 45, entertainment: 100, singlePersonMonthly: 1250 },
      'Lyon':        { rent1BedCenter:  850, rent1BedOutside:  650, utilities: 160, internet: 32, transportPass: 65, mealInexpensive: 14, mealMidRange: 45, groceriesMonthly: 300, gymMembership: 40, entertainment:  85, singlePersonMonthly:  900 },
      'Marseille':   { rent1BedCenter:  750, rent1BedOutside:  580, utilities: 160, internet: 32, transportPass: 55, mealInexpensive: 13, mealMidRange: 42, groceriesMonthly: 290, gymMembership: 40, entertainment:  80, singlePersonMonthly:  850 },
      'Toulouse':    { rent1BedCenter:  700, rent1BedOutside:  540, utilities: 150, internet: 32, transportPass: 55, mealInexpensive: 13, mealMidRange: 42, groceriesMonthly: 280, gymMembership: 40, entertainment:  80, singlePersonMonthly:  820 },
      'Bordeaux':    { rent1BedCenter:  800, rent1BedOutside:  620, utilities: 155, internet: 32, transportPass: 55, mealInexpensive: 14, mealMidRange: 45, groceriesMonthly: 290, gymMembership: 40, entertainment:  85, singlePersonMonthly:  880 },
      'Nice':        { rent1BedCenter:  950, rent1BedOutside:  720, utilities: 165, internet: 33, transportPass: 45, mealInexpensive: 15, mealMidRange: 48, groceriesMonthly: 310, gymMembership: 42, entertainment:  90, singlePersonMonthly:  980 },
    },
  },

  singapore: {
    currency: 'SGD',
    symbol: 'S$',
    cities: {
      'Singapore':   { rent1BedCenter: 3300, rent1BedOutside: 2400, utilities: 220, internet: 45, transportPass: 130, mealInexpensive: 15, mealMidRange: 45, groceriesMonthly: 450, gymMembership: 100, entertainment: 200, singlePersonMonthly: 1500 },
      'Jurong':      { rent1BedCenter: 2400, rent1BedOutside: 1900, utilities: 200, internet: 45, transportPass: 125, mealInexpensive: 12, mealMidRange: 35, groceriesMonthly: 400, gymMembership:  90, entertainment: 180, singlePersonMonthly: 1300 },
      'Woodlands':   { rent1BedCenter: 2200, rent1BedOutside: 1750, utilities: 195, internet: 45, transportPass: 120, mealInexpensive: 12, mealMidRange: 32, groceriesMonthly: 390, gymMembership:  85, entertainment: 170, singlePersonMonthly: 1200 },
    },
  },

  malaysia: {
    currency: 'MYR',
    symbol: 'RM ',
    cities: {
      'Kuala Lumpur': { rent1BedCenter: 2400, rent1BedOutside: 1500, utilities: 200, internet: 130, transportPass: 100, mealInexpensive: 15, mealMidRange: 60, groceriesMonthly: 500, gymMembership: 150, entertainment: 200, singlePersonMonthly: 2500 },
      'Penang':       { rent1BedCenter: 1500, rent1BedOutside: 1000, utilities: 180, internet: 120, transportPass:  80, mealInexpensive: 12, mealMidRange: 45, groceriesMonthly: 420, gymMembership: 120, entertainment: 160, singlePersonMonthly: 1900 },
      'Johor Bahru':  { rent1BedCenter: 1400, rent1BedOutside:  900, utilities: 170, internet: 120, transportPass:  75, mealInexpensive: 12, mealMidRange: 42, groceriesMonthly: 400, gymMembership: 110, entertainment: 150, singlePersonMonthly: 1800 },
      'Cyberjaya':    { rent1BedCenter: 1600, rent1BedOutside: 1200, utilities: 185, internet: 130, transportPass:  85, mealInexpensive: 13, mealMidRange: 45, groceriesMonthly: 430, gymMembership: 130, entertainment: 165, singlePersonMonthly: 2000 },
    },
  }
};

// Exchange rates to INR (approximate, should be updated via API)
const exchangeRates = {
  USD: 83.5,
  GBP: 106.0,
  CAD: 62.0,
  AUD: 54.5,
  EUR: 90.5,
  NZD: 51.0,
  AED: 22.7,
  SGD: 62.0,
  MYR: 17.8,
  INR: 1
};

const lifestyleMultipliers = {
  budget: 0.7,
  moderate: 1.0,
  comfortable: 1.4
};

const CostOfLivingCalculator = ({ country = 'usa', countryName = 'USA' }) => {
  const { isDark } = useTheme();
  const [selectedCity, setSelectedCity] = useState('');
  const [lifestyle, setLifestyle] = useState('moderate');
  const [accommodation, setAccommodation] = useState('outside'); // 'center' or 'outside'
  const [displayCurrency, setDisplayCurrency] = useState('local');
  
  const countryData = costData[country.toLowerCase()] || costData.usa;
  const cities = Object.keys(countryData.cities);
  
  useEffect(() => {
    if (cities.length > 0 && !selectedCity) {
      setSelectedCity(cities[0]);
    }
  }, [cities, selectedCity]);

  // Memoize cityData to ensure stable reference
  const cityData = useMemo(() => {
    return countryData.cities[selectedCity] || countryData.cities[cities[0]] || null;
  }, [countryData.cities, selectedCity, cities]);
  
  const calculations = useMemo(() => {
    if (!cityData) return null;
    
    const multiplier = lifestyleMultipliers[lifestyle];
    const rent = accommodation === 'center' ? cityData.rent1BedCenter : cityData.rent1BedOutside;
    
    const breakdown = {
      rent: Math.round(rent),
      utilities: Math.round(cityData.utilities * multiplier),
      internet: Math.round(cityData.internet),
      transport: Math.round(cityData.transportPass),
      groceries: Math.round(cityData.groceriesMonthly * multiplier),
      diningOut: Math.round((lifestyle === 'budget' ? 2 : lifestyle === 'moderate' ? 4 : 8) * cityData.mealInexpensive),
      gym: lifestyle === 'budget' ? 0 : Math.round(cityData.gymMembership),
      entertainment: Math.round(cityData.entertainment * multiplier),
      miscellaneous: Math.round(100 * multiplier)
    };
    
    const total = Object.values(breakdown).reduce((sum, val) => sum + val, 0);
    
    return { breakdown, total };
  }, [cityData, lifestyle, accommodation]);

  const formatCurrency = (amount, showINR = false) => {
    if (!amount) return '-';
    
    if (displayCurrency === 'inr' || showINR) {
      const inrAmount = amount * exchangeRates[countryData.currency];
      return `₹${Math.round(inrAmount).toLocaleString()}`;
    }
    return `${countryData.symbol}${Math.round(amount).toLocaleString()}`;
  };

  const expenseCategories = [
    { key: 'rent', label: 'Rent', icon: Home, color: 'from-rose-500 to-pink-500' },
    { key: 'utilities', label: 'Utilities', icon: Wifi, color: 'from-amber-500 to-orange-500' },
    { key: 'internet', label: 'Internet', icon: Wifi, color: 'from-cyan-500 to-blue-500' },
    { key: 'transport', label: 'Transport', icon: Bus, color: 'from-green-500 to-emerald-500' },
    { key: 'groceries', label: 'Groceries', icon: ShoppingBag, color: 'from-violet-500 to-purple-500' },
    { key: 'diningOut', label: 'Dining Out', icon: Utensils, color: 'from-red-500 to-rose-500' },
    { key: 'gym', label: 'Fitness', icon: Dumbbell, color: 'from-blue-500 to-indigo-500' },
    { key: 'entertainment', label: 'Entertainment', icon: TrendingUp, color: 'from-pink-500 to-rose-500' },
    { key: 'miscellaneous', label: 'Miscellaneous', icon: DollarSign, color: 'from-slate-500 to-gray-500' }
  ];

  if (!calculations) return null;

  return (
    <section 
      className={`py-16 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}
      data-testid="cost-of-living-calculator"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className={`mb-4 ${isDark ? 'bg-ux-green/20 text-ux-green' : 'bg-ux-green/10 text-ux-green'}`}>
            <Calculator className="w-3 h-3 mr-1" />
            Cost Calculator
          </Badge>
          <h2 className={`text-3xl md:text-4xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            Monthly Budget
            <span className="gradient-text-electric"> Estimator</span>
          </h2>
          <p className={`mt-3 text-base max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
            Plan your finances with real cost of living data from {countryName}
          </p>
        </div>

        {/* Controls */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {/* City Selector */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
              City
            </label>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className={`${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-ux-slate-light'}`}>
                <SelectValue placeholder="Select city" />
              </SelectTrigger>
              <SelectContent>
                {cities.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lifestyle Selector */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
              Lifestyle
            </label>
            <Select value={lifestyle} onValueChange={setLifestyle}>
              <SelectTrigger className={`${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-ux-slate-light'}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="budget">Budget-Friendly</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="comfortable">Comfortable</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Accommodation Type */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
              Accommodation
            </label>
            <Select value={accommodation} onValueChange={setAccommodation}>
              <SelectTrigger className={`${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-ux-slate-light'}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="center">City Center</SelectItem>
                <SelectItem value="outside">Outside Center</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Currency Toggle */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
              Display Currency
            </label>
            <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
              <SelectTrigger className={`${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-ux-slate-light'}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="local">{countryData.currency}</SelectItem>
                <SelectItem value="inr">INR (₹)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Expense Breakdown */}
          <div className="lg:col-span-2">
            <div className={`glass-panel rounded-3xl p-6 ${isDark ? '' : 'bg-white shadow-lg'}`}>
              <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Monthly Expense Breakdown
              </h3>
              
              <div className="space-y-4">
                {expenseCategories.map(({ key, label, icon: Icon, color }) => {
                  const amount = calculations.breakdown[key];
                  // Use max amount as baseline for percentage to show relative differences
                  const maxAmount = Math.max(...Object.values(calculations.breakdown));
                  const percentage = (amount / maxAmount) * 100;
                  
                  return (
                    <div key={`${key}-${selectedCity}-${lifestyle}-${accommodation}`} className="group">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                            {label}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                            {formatCurrency(amount)}
                          </span>
                          {displayCurrency === 'local' && (
                            <span className={`block text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                              {formatCurrency(amount, true)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Bar - relative to highest expense */}
                      <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-ux-slate-light'}`}>
                        <div 
                          className={`h-full rounded-full bg-gradient-to-r ${color}`}
                          style={{ 
                            width: `${Math.min(percentage, 100)}%`,
                            transition: 'width 0.5s ease-out'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Total Summary */}
          <div className="space-y-6">
            {/* Total Card */}
            <div className="glass-panel rounded-3xl p-6 bg-gradient-to-br from-ux-cyan/20 to-ux-blue/20 border border-ux-cyan/30">
              <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                Estimated Monthly Total
              </h3>
              <div className="text-4xl font-bold gradient-text-electric mb-2">
                {formatCurrency(calculations.total)}
              </div>
              {displayCurrency === 'local' && (
                <div className={`text-lg ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  ≈ {formatCurrency(calculations.total, true)}
                </div>
              )}
              
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-ux-slate-light'}`}>
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>Annual Estimate</span>
                  <span className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {formatCurrency(calculations.total * 12)}
                  </span>
                </div>
              </div>
            </div>

            {/* Tips Card */}
            <div className={`glass-panel rounded-2xl p-5 ${isDark ? '' : 'bg-ux-slate-light/50'}`}>
              <div className="flex items-start gap-3">
                <Info className={`w-5 h-5 mt-0.5 ${isDark ? 'text-ux-amber' : 'text-ux-amber'}`} />
                <div>
                  <h4 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Student Tips
                  </h4>
                  <ul className={`text-sm space-y-1 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    <li>• Shared accommodation can reduce rent by 40-60%</li>
                    <li>• Student discounts available on transport</li>
                    <li>• Campus facilities reduce gym costs</li>
                    <li>• Cook at home to save on food expenses</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Data Source */}
            <p className={`text-xs text-center ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
              Data sourced from Numbeo • Last updated: Feb 2026
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostOfLivingCalculator;
